import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { 
  Package, 
  Filter, 
  Eye, 
  ChevronDown, 
  ChevronRight, 
  ExpandIcon, 
  Minimize2 
} from 'lucide-react';
import { getJson } from '@/lib/http';
import { EnhancedProofModal } from './proof-of-posting/EnhancedProofModal';

interface FormatDiscrepancy {
  campaignId: number;
  campaignName: string;
  storeId: number;
  storeName: string;
  formatId: number;
  formatName: string;
  expectedCount: number;
  actualCount: number | null;
  discrepancyReason: string | null;
  operatorId: number;
  operatorName: string;
  photoUrl: string | null;
  postingEventId: number;
  evidenceAssetId: number | null;
  packId?: number;
  packName?: string;
  taskId?: number;
  cycleId?: number;
  assignmentId?: number;
  status?: string | null; // Task completion status for filtering
}

interface StoreGroup {
  storeId: number;
  storeName: string;
  discrepancies: FormatDiscrepancy[];
  shortfallCount: number;
  summaryText: string;
  summaryColor: 'gray' | 'orange' | 'green';
}

interface FormatDiscrepanciesTabProps {
  campaignId: number;
}

export function FormatDiscrepanciesTab({ campaignId }: FormatDiscrepanciesTabProps) {
  const [filterStore, setFilterStore] = useState<string>("");
  const [filterOperator, setFilterOperator] = useState<string>("all");
  const [filterFormat, setFilterFormat] = useState<string>("all");
  const [selectedPhoto, setSelectedPhoto] = useState<string | null>(null);
  const [expandedStores, setExpandedStores] = useState<string[]>([]);
  const [selectedAssignmentForEvidence, setSelectedAssignmentForEvidence] = useState<{
    storeId: number;
    cycleId: number;
    operatorId: number;
    assignmentId: number;
  } | null>(null);

  // Fetch per-format delivery & compliance
  const { data: discrepancyData = [], isLoading, error } = useQuery({
    queryKey: [`/api/admin/format-discrepancies/${campaignId}`],
    queryFn: () => getJson(`/api/admin/format-discrepancies/${campaignId}`),
    staleTime: 0, // Always consider data stale to ensure fresh status
    refetchOnMount: 'always', // Refetch on component mount
    refetchOnWindowFocus: true // Refetch when window gains focus
  });

  // Fetch all discrepancies for filtering options
  const { data: allDiscrepancies = [] } = useQuery({
    queryKey: ["/api/admin/delivery-compliance"],
    queryFn: () => Promise.resolve([]),
  });

  // Convert backend response (already per-format) to view model shape
  const discrepancies = useMemo(() => {
    if (!Array.isArray(discrepancyData)) return [];
    return discrepancyData.map((row: any): FormatDiscrepancy => ({
      campaignId: Number(row.campaignId) || campaignId,
      campaignName: row.campaignName || '',
      storeId: Number(row.storeId),
      storeName: row.storeName,
      formatId: Number(row.formatId),
      formatName: row.formatName,
      expectedCount: Number(row.expectedCount) || 0,
      actualCount: row.actualCount == null ? null : Number(row.actualCount),
      discrepancyReason: row.discrepancyReason ?? null,
      operatorId: Number(row.operatorId),
      operatorName: row.operatorName || `Operator ${row.operatorId}`,
      photoUrl: row.proofImageUrl || null,
      postingEventId: row.postingEventId || 0,
      evidenceAssetId: null,
      packId: row.packId ?? null,
      packName: row.packName ?? null,
      taskId: Number(row.assignmentId),
      cycleId: row.cycleId ?? 0,
      assignmentId: Number(row.assignmentId),
      status: row.status || null // Task completion status
    }));
  }, [discrepancyData, campaignId]);

  // Get unique values for filter dropdowns from current campaign dataset
  const uniqueStores = useMemo(() =>
    Array.from(new Set(discrepancies.map((d: FormatDiscrepancy) => d.storeName).filter(Boolean))).sort()
  , [discrepancies]);

  const uniqueOperators = useMemo(() =>
    Array.from(new Set(
      discrepancies
        .map((d: FormatDiscrepancy) => (d.operatorName && d.operatorName.trim() !== "") ? d.operatorName : `Operator ${d.operatorId}`)
        .filter((name: string) => !!name && name.trim() !== "")
    )).sort()
  , [discrepancies]);

  const uniqueFormats = useMemo(() =>
    Array.from(new Set(
      discrepancies
        .map((d: FormatDiscrepancy) => d.formatName)
        .filter((name: string) => !!name && name.trim() !== "")
    )).sort()
  , [discrepancies]);

  // Filter discrepancies based on selected filters
  const filteredDiscrepancies = discrepancies.filter((discrepancy: FormatDiscrepancy) => {
    const operatorLabel = (discrepancy.operatorName && discrepancy.operatorName.trim() !== "")
      ? discrepancy.operatorName
      : `Operator ${discrepancy.operatorId}`;
    return (
      (filterStore === "" || discrepancy.storeName?.includes(filterStore)) &&
      (filterOperator === "" || filterOperator === "all" || operatorLabel === filterOperator) &&
      (filterFormat === "" || filterFormat === "all" || discrepancy.formatName === filterFormat)
    );
  });

  // Group discrepancies by store
  const storeGroups = useMemo(() => {
    const groups = new Map<number, StoreGroup>();
    const completedStatuses = ['operator_complete', 'admin_complete', 'locked'];
    
    filteredDiscrepancies.forEach((discrepancy: FormatDiscrepancy) => {
      const storeId = discrepancy.storeId;
      const storeName = discrepancy.storeName;
      
      if (!groups.has(storeId)) {
        groups.set(storeId, {
          storeId,
          storeName,
          discrepancies: [],
          shortfallCount: 0,
          summaryText: '',
          summaryColor: 'gray'
        });
      }
      
      const group = groups.get(storeId)!;
      group.discrepancies.push(discrepancy);
    });
    
    // Compute summary for each store group
    Array.from(groups.values()).forEach(group => {
      // Check if all tasks are completed
      const allCompleted = group.discrepancies.every((discrepancy: FormatDiscrepancy) => {
        const status = discrepancy.status || '';
        return completedStatuses.includes(status);
      });
      
      if (!allCompleted) {
        // Any task is not completed
        group.summaryText = 'Pending Completion';
        group.summaryColor = 'gray';
        group.shortfallCount = 0; // Don't count shortfalls until all tasks are completed
      } else {
        // All tasks are completed - count confirmed shortfalls
        group.shortfallCount = 0;
        group.discrepancies.forEach((discrepancy: FormatDiscrepancy) => {
          const delivered = discrepancy.actualCount ?? 0;
          const expected = discrepancy.expectedCount;
          if (delivered < expected) {
            group.shortfallCount += 1;
          }
        });
        
        if (group.shortfallCount > 0) {
          group.summaryText = `${group.shortfallCount} confirmed shortfall${group.shortfallCount !== 1 ? 's' : ''}`;
          group.summaryColor = 'orange';
        } else {
          group.summaryText = 'Fully Delivered';
          group.summaryColor = 'green';
        }
      }
      
      // Sort discrepancies within each group by format name
      group.discrepancies.sort((a, b) => a.formatName.localeCompare(b.formatName));
    });
    
    return Array.from(groups.values()).sort((a, b) => a.storeName.localeCompare(b.storeName));
  }, [filteredDiscrepancies]);

  // Helper functions
  const getOperatorAvatarInitials = (operatorName: string): string => {
    if (!operatorName) return '?';
    const names = operatorName.split(/[\s_]+/);
    if (names.length >= 2) {
      return (names[0][0] + names[1][0]).toUpperCase();
    }
    return operatorName.substring(0, 2).toUpperCase();
  };

  const getStatusVariant = (status: string) => {
    switch (status) {
      case 'Shortfall': return 'destructive';
      case 'Pending Completion': return 'outline';
      case 'On Target': return 'default';
      case 'Excess': return 'secondary';
      default: return 'outline';
    }
  };

  const getStatus = (discrepancy: FormatDiscrepancy): string => {
    const isComplete = ['operator_complete', 'admin_complete', 'locked'].includes(discrepancy.status || '');
    if (!isComplete) {
      return 'Pending Completion';
    }
    
    const delivered = discrepancy.actualCount ?? 0;
    const expected = discrepancy.expectedCount;
    
    if (delivered < expected) return 'Shortfall';
    if (delivered > expected) return 'Excess';
    return 'On Target';
  };

  const getStatusTooltip = (discrepancy: FormatDiscrepancy): string => {
    const isComplete = ['operator_complete', 'admin_complete', 'locked'].includes(discrepancy.status || '');
    if (!isComplete) {
      return "Task is not yet completed by operator";
    }
    
    const delivered = discrepancy.actualCount ?? 0;
    const expected = discrepancy.expectedCount;
    
    if (delivered < expected) {
      return "Expected > Actual. Operator marked task complete";
    }
    if (delivered > expected) {
      return "Actual > Expected";
    }
    return "Expected = Actual";
  };

  const toggleStoreExpansion = (storeId: number) => {
    const storeKey = storeId.toString();
    setExpandedStores(prev => 
      prev.includes(storeKey) 
        ? prev.filter(id => id !== storeKey)
        : [...prev, storeKey]
    );
  };

  const expandAllStores = () => {
    setExpandedStores(storeGroups.map(group => group.storeId.toString()));
  };

  const collapseAllStores = () => {
    setExpandedStores([]);
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center">
            <div className="text-muted-foreground">Loading format discrepancies...</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center">
            <div className="text-destructive">Error loading format discrepancies</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <TooltipProvider>
      <div className="space-y-4">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <div className="flex flex-col gap-1">
                <div className="flex items-center gap-2">
                  <Package className="h-5 w-5" />
                  Delivery & Compliance (mobile)
                </div>
                <p className="text-sm font-normal text-muted-foreground">
                  Expected vs delivered counts per format for all assigned stores.
                </p>
              </div>
              <Badge variant="destructive" className="ml-2">
                {filteredDiscrepancies.length}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* Filter Controls */}
            <div className="flex flex-wrap gap-4 mb-6 p-4 bg-muted/50 rounded-lg">
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">Filters:</span>
              </div>
              
              <div className="flex flex-wrap gap-4">
                <div className="min-w-[200px]">
                  <Input
                    placeholder="Search stores..."
                    value={filterStore}
                    onChange={(e) => setFilterStore(e.target.value)}
                    className="h-8"
                  />
                </div>
                
                <Select value={filterOperator} onValueChange={setFilterOperator}>
                  <SelectTrigger className="w-[180px] h-8">
                    <SelectValue placeholder="All Operators" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Operators</SelectItem>
                    {uniqueOperators.map(operator => (
                      <SelectItem key={operator} value={operator}>{operator}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                <Select value={filterFormat} onValueChange={setFilterFormat}>
                  <SelectTrigger className="w-[180px] h-8">
                    <SelectValue placeholder="All Formats" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Formats</SelectItem>
                    {uniqueFormats.map(format => (
                      <SelectItem key={format} value={format}>{format}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* No Results */}
            {filteredDiscrepancies.length === 0 && (
              <div className="text-center py-8">
                <div className="text-muted-foreground">No records to display.</div>
              </div>
            )}

            {/* Expand/Collapse All Controls */}
            {storeGroups.length > 1 && (
              <div className="flex justify-between items-center mb-4">
                <div className="text-sm text-muted-foreground">
                  {storeGroups.length} stores listed
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={expandAllStores}
                    className="flex items-center gap-1"
                  >
                    <ExpandIcon className="h-3 w-3" />
                    Expand All
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={collapseAllStores}
                    className="flex items-center gap-1"
                  >
                    <Minimize2 className="h-3 w-3" />
                    Collapse All
                  </Button>
                </div>
              </div>
            )}

            {/* Store-grouped Display */}
            <div className="space-y-3">
              {storeGroups.map((storeGroup) => {
                const isExpanded = expandedStores.includes(storeGroup.storeId.toString());
                
                return (
                  <Collapsible key={storeGroup.storeId} open={isExpanded} onOpenChange={() => toggleStoreExpansion(storeGroup.storeId)}>
                    <CollapsibleTrigger asChild>
                      <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg hover:bg-muted/50 cursor-pointer transition-colors">
                        <div className="flex items-center gap-3">
                          <div className="flex items-center gap-2">
                            {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                            <span className="font-medium">{storeGroup.storeName}</span>
                          </div>
                          <Badge 
                            variant="outline" 
                            className={`text-xs px-2 py-1 ${
                              storeGroup.summaryColor === 'gray'
                                ? 'bg-gray-100 text-gray-700 border-gray-300'
                                : storeGroup.summaryColor === 'orange'
                                ? 'bg-orange-50 text-orange-700 border-orange-300'
                                : 'bg-green-50 text-green-700 border-green-300'
                            }`}
                          >
                            {storeGroup.summaryText}
                          </Badge>
                        </div>
                      </div>
                    </CollapsibleTrigger>
                    
                    <CollapsibleContent className="mt-2">
                      <div className="bg-white rounded-lg border">
                        <Table>
                          <TableHeader className="bg-muted/20">
                            <TableRow>
                              <TableHead className="text-xs font-medium w-[80px]">Assignment ID</TableHead>
                              <TableHead className="text-xs font-medium w-[220px]">Format</TableHead>
                              <TableHead className="text-xs font-medium w-[200px]">Operator</TableHead>
                              <TableHead className="text-xs font-medium text-right w-[80px]">Expected</TableHead>
                              <TableHead className="text-xs font-medium text-right w-[80px]">Delivered</TableHead>
                              <TableHead className="text-xs font-medium w-[100px]">Status</TableHead>
                              <TableHead className="text-xs font-medium w-[140px]">Evidence</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {(() => {
                              const shownEvidenceForAssignment = new Set<number>();
                              let previousAssignmentId: number | null = null;
                              return storeGroup.discrepancies.map((discrepancy, index) => {
                              const status = getStatus(discrepancy);
                                const canShowEvidence = !shownEvidenceForAssignment.has(discrepancy.assignmentId || 0);
                                if (canShowEvidence) {
                                  shownEvidenceForAssignment.add(discrepancy.assignmentId || 0);
                                }
                                
                                // Show Assignment ID only on first row or when it changes
                                const showAssignmentId = previousAssignmentId !== (discrepancy.assignmentId ?? null);
                                previousAssignmentId = discrepancy.assignmentId ?? null;
                              return (
                                <TableRow key={`${discrepancy.storeId}-${discrepancy.assignmentId}-${index}`} className="text-sm">
                                  <TableCell className="py-1.5 text-center font-mono text-xs font-semibold">
                                    {showAssignmentId ? discrepancy.assignmentId : ''}
                                  </TableCell>
                                  <TableCell className="py-1.5">
                                    <div className="flex items-center gap-2">
                                      <Badge variant="outline" className="text-xs">
                                        {discrepancy.formatName}
                                      </Badge>
                                    </div>
                                  </TableCell>
                                  <TableCell className="py-1.5 font-medium">
                                    {discrepancy.operatorName || `Operator ${discrepancy.operatorId}`}
                                  </TableCell>
                                  <TableCell className="py-1.5 text-right font-mono text-xs">
                                    {discrepancy.expectedCount}
                                  </TableCell>
                                  <TableCell className="py-1.5 text-right font-mono text-xs">
                                    {discrepancy.actualCount ?? '—'}
                                  </TableCell>
                                  <TableCell className="py-1.5">
                                    <div className="flex items-center gap-1">
                                      <Tooltip>
                                        <TooltipTrigger>
                                          <Badge 
                                            variant={getStatusVariant(status)} 
                                            className={`text-xs px-1.5 py-0.5 ${
                                              status === "Shortfall" 
                                                ? "border-amber-500 text-amber-700 bg-amber-50" 
                                                : status === "Pending Completion"
                                                ? "border-blue-500 text-blue-700 bg-blue-50"
                                                : ""
                                            }`}
                                          >
                                            {status}
                                          </Badge>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                          <p>{getStatusTooltip(discrepancy)}</p>
                                        </TooltipContent>
                                      </Tooltip>
                                    </div>
                                  </TableCell>
                                  <TableCell className="py-1.5">
                                      {canShowEvidence ? (
                                        <Button 
                                          variant="outline" 
                                          size="sm" 
                                          onClick={() => setSelectedAssignmentForEvidence({
                                            storeId: discrepancy.storeId,
                                            cycleId: discrepancy.cycleId ?? 0,
                                            operatorId: discrepancy.operatorId,
                                            assignmentId: discrepancy.assignmentId ?? discrepancy.taskId ?? 0
                                          })}
                                        >
                                          <Eye className="h-3 w-3 mr-1" />
                                            View Evidence
                                          </Button>
                                      ) : (
                                        <span className="text-xs text-muted-foreground">—</span>
                                      )}
                                  </TableCell>
                                </TableRow>
                              );
                            });
                            })()}
                          </TableBody>
                        </Table>
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Photo Modal */}
        <Dialog open={!!selectedPhoto} onOpenChange={() => setSelectedPhoto(null)}>
          <DialogContent className="max-w-4xl">
            <DialogHeader>
              <DialogTitle>Evidence Photo</DialogTitle>
            </DialogHeader>
            {selectedPhoto && (
              <div className="flex justify-center">
                <img
                  src={selectedPhoto}
                  alt="Evidence"
                  className="max-w-full max-h-[70vh] object-contain rounded"
                />
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Evidence Modal for Assignment */}
        {selectedAssignmentForEvidence && (
          <EnhancedProofModal
            isOpen={!!selectedAssignmentForEvidence}
            onClose={() => setSelectedAssignmentForEvidence(null)}
            storeId={selectedAssignmentForEvidence.storeId}
            cycleId={selectedAssignmentForEvidence.cycleId}
            operatorId={selectedAssignmentForEvidence.operatorId}
            assignmentId={selectedAssignmentForEvidence.assignmentId}
            onSuccess={() => setSelectedAssignmentForEvidence(null)}
            refetch={() => {
              // Refetch discrepancies when evidence is updated
            }}
          />
        )}
      </div>
    </TooltipProvider>
  );
}